// 표 셀 수준 데이터 정의(값 바인딩 / 계산 / 집계) 수집 + 한글 요약 헬퍼.
//
// 캔버스 바인딩 배지 오버레이(BindingBadgeOverlay)가 노드 바인딩(ComposerNode.binding)뿐 아니라
// 표 셀 정의(TableCell.binding/compute/aggregate)에도 배지를 띄우도록, table 노드의 cells를 순회해
// "실제로 표시되는 값 소스"가 있는 셀을 모은다.
//
// 렌더 우선순위(RenderTemplate.renderCell)와 동일하게 판정한다:
//   content > (비어있지 않은)text > aggregate > compute > binding
// content 셀은 내부 노드가 이미 노드 배지를 받으므로 셀 배지 대상에서 제외하고, 비어있지 않은 정적
// text 셀은 값 소스가 가려지므로 제외한다(가짜 배지 방지).

import {
  effectiveCellSourceKind,
  COMPUTE_OP_PLACEHOLDER_LABEL,
  AGGREGATE_FN_PLACEHOLDER_LABEL,
  type CellSourceKind,
  type ComposerNode,
} from '@safetyhub/report-composer';
import type {
  Binding,
  BindingFormat,
  CatalogEntry,
  CellAggregate,
  CellCompute,
} from '@safetyhub/shared';
import { buildBindingSummary } from './bindingSummary';
import { walkNodeTree } from './nodeTreeWalk';

/** 값 소스가 있는 셀 1개 — 정체성 좌표(tableId::row::col) + 종류 + 원본 정의. */
export interface BoundCell {
  tableId: string;
  row: number;
  col: number;
  kind: CellSourceKind;
  binding?: Binding;
  compute?: CellCompute;
  aggregate?: CellAggregate;
}

/**
 * body 전체를 순회해 값 소스가 있는 표 셀을 모은다(중첩 표/반복 template 안 표 포함 — walkNodeTree 기본 하강).
 * 반복 전개 복제 셀은 DOM에서 같은 정체성 좌표를 공유하므로, 여기서는 정의(패턴 셀)만 모으고 배지 렌더
 * 단계에서 DOM 요소 개수만큼 배지를 그린다.
 *
 * 셀 값소스 판정은 rc effectiveCellSourceKind로 일원화한다(renderTableCell 렌더 분기와 동일 판정 — 배지↔렌더 정합).
 */
export function collectBoundCells(body: ComposerNode[]): BoundCell[] {
  const out: BoundCell[] = [];
  walkNodeTree(body, (n) => {
    if (n.kind !== 'table') return;
    for (const cell of n.cells) {
      const kind = effectiveCellSourceKind(cell);
      if (!kind) continue;
      out.push({
        tableId: n.id,
        row: cell.row,
        col: cell.col,
        kind,
        binding: cell.binding,
        compute: cell.compute,
        aggregate: cell.aggregate,
      });
    }
  });
  return out;
}

/** 정체성 좌표 문자열(data-composer-cell attribute 값과 동형). */
export function cellCoordKey(tableId: string, row: number, col: number): string {
  return `${tableId}::${row}::${col}`;
}

// ── 요약 텍스트 ──
// op/fn → 한글 라벨 맵은 rc placeholder 모듈과 공유(COMPUTE_OP_PLACEHOLDER_LABEL/AGGREGATE_FN_PLACEHOLDER_LABEL).

/** BindingFormat → 짧은 한글 요약(툴팁 후미). 미지정이면 undefined. */
export function summarizeFormat(fmt?: BindingFormat): string | undefined {
  if (!fmt) return undefined;
  switch (fmt.kind) {
    case 'unit':
      return `단위 ${fmt.unit ?? fmt.unitPath ?? ''}`.trim();
    case 'date':
      return '날짜';
    case 'url':
      return 'URL';
    case 'template':
      return '템플릿';
    case 'default':
      return `기본값(${fmt.fallback})`;
    case 'join':
      return '목록 결합';
    case 'map':
      return '값 매핑';
    case 'switch':
      return `분기(${fmt.on})`;
    case 'rangeMap': {
      // 구간 배열 출처 표기 — 해소 우선순위(rangesFrom → rangesKey)와 같은 순서로 보여준다.
      const from = fmt.rangesFrom
        ? `${fmt.rangesFrom.catalogKey}${fmt.rangesFrom.select ? `.${fmt.rangesFrom.select}` : ''}`
        : fmt.rangesKey;
      // 병기 축은 셀에 값이 하나 더 붙는다는 뜻이라 요약에서도 보여야 한다(안 보이면 저작자가
      // 그 칸에 왜 두 값이 나오는지 요약만으로는 알 수 없다). 미지정이면 종전 그대로.
      const annotation = fmt.annotationKey ? ` + 병기(${fmt.annotationKey})` : '';
      return `구간 판정${from ? `(${from})` : ''}${annotation}`;
    }
    case 'bool':
      return '예/아니오';
    default:
      return undefined;
  }
}

/** 계산 셀 요약 — "계산: {op/식} · 입력 N개 → {format}". */
export function buildComputeSummary(compute: CellCompute): string {
  const label =
    compute.mode === 'expr'
      ? compute.expr
        ? `식 ${compute.expr}`
        : '계산식'
      : (compute.op && COMPUTE_OP_PLACEHOLDER_LABEL[compute.op]) ?? '계산';
  const n = compute.inputs.length;
  const fmt = summarizeFormat(compute.format);
  return `계산: ${label} · 입력 ${n}개${fmt ? ` → ${fmt}` : ''}`;
}

/** 집계 셀 요약 — "집계: {fn}({over[.field]}) → {format}". */
export function buildAggregateSummary(agg: CellAggregate): string {
  const fn = AGGREGATE_FN_PLACEHOLDER_LABEL[agg.fn] ?? '집계';
  const src = agg.field ? `${agg.over}.${agg.field}` : agg.over;
  const fmt = summarizeFormat(agg.format);
  return `집계: ${fn}(${src})${fmt ? ` → ${fmt}` : ''}`;
}

/** BoundCell → 배지 hover 툴팁 텍스트. */
export function buildCellDefTooltip(def: BoundCell, catalog: CatalogEntry[]): string {
  if (def.kind === 'aggregate' && def.aggregate) return buildAggregateSummary(def.aggregate);
  if (def.kind === 'compute' && def.compute) return buildComputeSummary(def.compute);
  if (def.kind === 'binding' && def.binding) return buildBindingSummary(def.binding, catalog).text;
  return '';
}
